/**
 * When an application backs itself up, if it does.
 *
 * The form holds what somebody typed, in their own time, and converts on the
 * way out -- see `schedule.ts`, which is where every part of that lives and none
 * of it is React. What the server says about the schedule it stored, it says in
 * `next_run_at`, and that is what the section reports: it is the one answer to
 * "when will this actually run" that no conversion here can be wrong about.
 */
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import type { SnapshotDestination, SnapshotSchedule } from '@/api/types'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'
import { NativeSelect } from '@/shared/ui/native-select'
import { Switch } from '@/shared/ui/switch'
import { whenText } from '@/shared/lib/when'
import {
  DEFAULT_KEEP_LAST,
  SNAPSHOT_INTERVALS,
  defaultSchedule,
  scheduleUses,
  toLocal,
  toStored,
  type LocalSchedule,
  type SnapshotInterval,
} from '../schedule'
import {
  useRemoveSnapshotSchedule,
  useSetSnapshotSchedule,
  useSnapshotSchedule,
} from '../queries'

/** Monday first, as the wire and the form both count. */
const WEEKDAY_KEYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const

const TIMEZONE = (): string => Intl.DateTimeFormat().resolvedOptions().timeZone

export function ScheduleSection({
  applicationId,
  destinations,
}: {
  applicationId: number
  destinations: SnapshotDestination[]
}) {
  const { t } = useTranslation()
  const { data: stored, isPending } = useSnapshotSchedule(applicationId)
  const save = useSetSnapshotSchedule(applicationId)
  const remove = useRemoveSnapshotSchedule(applicationId)

  const [draft, setDraft] = useState<LocalSchedule>(defaultSchedule)
  const [keepLast, setKeepLast] = useState(DEFAULT_KEEP_LAST)
  const [destination, setDestination] = useState<string | null>(null)

  // Filled from the server once it has answered, and only then. The form is
  // otherwise the reader's, so a background refetch must not take back what
  // they have typed -- which is why this depends on the schedule's identity
  // rather than on its contents.
  const storedId = stored?.id ?? null
  useEffect(() => {
    if (stored == null) return
    setDraft(
      toLocal(
        {
          minute: stored.minute ?? 0,
          hour: stored.hour ?? 0,
          day_of_week: stored.day_of_week ?? 0,
          day_of_month: stored.day_of_month ?? 1,
        },
        stored.interval,
        TIMEZONE(),
        new Date(),
      ),
    )
    setKeepLast(stored.keep_last ?? DEFAULT_KEEP_LAST)
    setDestination(stored.destination ?? null)
  }, [storedId])

  if (isPending) return null

  function submit() {
    const chosen = destination ?? destinations[0]?.type
    save.mutate({
      interval: draft.interval,
      ...toStored(draft, TIMEZONE(), new Date()),
      keep_last: keepLast,
      is_active: true,
      ...(chosen !== undefined && { destination: chosen }),
    })
  }

  return (
    <section aria-label={t('snapshot.scheduleTitle')} className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium">{t('snapshot.scheduleTitle')}</h3>
        <div className="flex items-center gap-2">
          <Label htmlFor="schedule-on" className="text-sm font-normal">
            {t('snapshot.scheduleOn')}
          </Label>
          <Switch
            id="schedule-on"
            checked={stored != null}
            disabled={save.isPending || remove.isPending}
            onCheckedChange={(on) => {
              if (on) submit()
              else remove.mutate()
            }}
          />
        </div>
      </div>

      {stored == null ? (
        <p className="text-sm text-muted-foreground">{t('snapshot.scheduleOff')}</p>
      ) : (
        <>
          {/*
            Deactivated by the server rather than by anybody here: a schedule
            that has failed three times running turns itself off. Saying so is
            the whole point -- a backup that stopped and said nothing is worse
            than no backup, because it is believed.
          */}
          {stored.automatically_deactivated && (
            <p className="text-sm text-destructive">{t('snapshot.deactivated')}</p>
          )}

          <div className="flex flex-wrap items-end gap-3">
            <div className="grid gap-1">
              <Label
                htmlFor="schedule-interval"
                className="text-xs text-muted-foreground"
              >
                {t('snapshot.every')}
              </Label>
              <NativeSelect
                id="schedule-interval"
                value={draft.interval}
                onChange={(event) =>
                  setDraft({
                    ...draft,
                    interval: event.target.value as SnapshotInterval,
                  })
                }
              >
                {SNAPSHOT_INTERVALS.map((interval) => (
                  <option key={interval} value={interval}>
                    {t(`snapshot.intervals.${interval}`)}
                  </option>
                ))}
              </NativeSelect>
            </div>

            {scheduleUses(draft.interval, 'dayOfWeek') && (
              <div className="grid gap-1">
                <Label
                  htmlFor="schedule-weekday"
                  className="text-xs text-muted-foreground"
                >
                  {t('snapshot.onDay')}
                </Label>
                <NativeSelect
                  id="schedule-weekday"
                  value={String(draft.dayOfWeek)}
                  onChange={(event) =>
                    setDraft({ ...draft, dayOfWeek: Number(event.target.value) })
                  }
                >
                  {WEEKDAY_KEYS.map((day, index) => (
                    <option key={day} value={index}>
                      {t(`snapshot.weekdays.${day}`)}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}

            {scheduleUses(draft.interval, 'dayOfMonth') && (
              <div className="grid gap-1">
                <Label htmlFor="schedule-date" className="text-xs text-muted-foreground">
                  {t('snapshot.onDate')}
                </Label>
                <Input
                  id="schedule-date"
                  type="number"
                  min={1}
                  max={31}
                  className="w-20"
                  value={draft.dayOfMonth}
                  onChange={(event) =>
                    setDraft({ ...draft, dayOfMonth: Number(event.target.value) })
                  }
                />
              </div>
            )}

            {scheduleUses(draft.interval, 'hour') && (
              <div className="grid gap-1">
                <Label htmlFor="schedule-hour" className="text-xs text-muted-foreground">
                  {t('snapshot.atHour')}
                </Label>
                <Input
                  id="schedule-hour"
                  type="number"
                  min={0}
                  max={23}
                  className="w-20"
                  value={draft.hour}
                  onChange={(event) =>
                    setDraft({ ...draft, hour: Number(event.target.value) })
                  }
                />
              </div>
            )}

            <div className="grid gap-1">
              <Label htmlFor="schedule-minute" className="text-xs text-muted-foreground">
                {t('snapshot.atMinute')}
              </Label>
              <Input
                id="schedule-minute"
                type="number"
                min={0}
                max={59}
                className="w-20"
                value={draft.minute}
                onChange={(event) =>
                  setDraft({ ...draft, minute: Number(event.target.value) })
                }
              />
            </div>

            <div className="grid gap-1">
              <Label htmlFor="schedule-keep" className="text-xs text-muted-foreground">
                {t('snapshot.keepLast')}
              </Label>
              <Input
                id="schedule-keep"
                type="number"
                min={1}
                max={1000}
                className="w-24"
                value={keepLast}
                onChange={(event) => setKeepLast(Number(event.target.value))}
              />
            </div>

            {/* Offered only where there is a choice: an instance with no bucket
                configured has exactly one destination. */}
            {destinations.length > 1 && (
              <div className="grid gap-1">
                <Label
                  htmlFor="schedule-destination"
                  className="text-xs text-muted-foreground"
                >
                  {t('snapshot.destination')}
                </Label>
                <NativeSelect
                  id="schedule-destination"
                  value={destination ?? destinations[0]?.type ?? ''}
                  onChange={(event) => setDestination(event.target.value)}
                >
                  {destinations.map((option) => (
                    <option key={option.type} value={option.type}>
                      {option.name}
                    </option>
                  ))}
                </NativeSelect>
              </div>
            )}

            <Button size="sm" disabled={save.isPending} onClick={submit}>
              {t('snapshot.saveSchedule')}
            </Button>
          </div>

          <NextRun schedule={stored} />
        </>
      )}
    </section>
  )
}

/**
 * When the server says it will next run, in the reader's own time.
 *
 * The authority on the whole conversion. Everything above turns local values
 * into UTC ones and could in principle be wrong about a clock change or a month
 * boundary; this is the server's own answer, read back.
 */
function NextRun({ schedule }: { schedule: SnapshotSchedule }) {
  const { t } = useTranslation()

  return (
    <p className="text-xs text-muted-foreground">
      {schedule.next_run_at == null
        ? t('snapshot.noNextRun')
        : t('snapshot.nextRun', { when: whenText(schedule.next_run_at) })}
      {schedule.last_run_at != null && (
        <> · {t('snapshot.lastRun', { when: whenText(schedule.last_run_at) })}</>
      )}
    </p>
  )
}
